import { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useAssistant } from "./useAssistant";
import { AssistantThread } from "./AssistantThread";
import { AssistantAskBox } from "./AssistantAskBox";
import { drawerChips } from "./assistantChips";

/** Client-side модуль-лейбли для бейджа (дзеркало backend MODULE_LABELS; лише
 *  whitelist-метадані сторінки — безпечно на клієнті). */
const MODULE_LABEL: Record<string, string> = {
  today: "Сьогодні", meetings: "Зустрічі", tasks: "Завдання", requests: "Запити",
  clients: "Клієнти", contacts: "Контакти", contracts: "Договори", complaints: "Рекламації",
  kb: "AI-помічник", "needs-review": "Профілі на перевірку", outlets: "Точки",
  "legal-entities": "Юрособи", profile: "Профіль", admin: "Адмін-центр",
  "sync-center": "Синхро-центр", settings: "Налаштування", home: "Головна",
};

function routeKeyFromPath(pathname: string): string | undefined {
  const seg = pathname.replace(/^\/+/, "").split("/")[0] ?? "";
  return seg in MODULE_LABEL ? seg : seg || undefined;
}

/**
 * «?»-drawer (TZ §4 / мокап v2) — помічник з будь-якого екрана. Контекст =
 * whitelist-метадані (routeKey сторінки); roleTier/accessMode НЕ передаються —
 * проксі виводить server-side. Бейдж показує «модуль · роль».
 */
export function HelpDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const routeKey = routeKeyFromPath(location.pathname);
  const moduleLabel = routeKey ? MODULE_LABEL[routeKey] : undefined;

  const panelRef = useDrawerA11y(open, onClose);
  const inputRef = useRef<HTMLInputElement>(null);
  const { thread, ask, asking } = useAssistant(routeKey);

  const lastResult = useMemo(() => {
    for (let i = thread.length - 1; i >= 0; i -= 1) {
      if (thread[i].result) return thread[i].result;
    }
    return undefined;
  }, [thread]);
  const directorMode = lastResult?.accessMode === "readOnly";
  const roleLabel = lastResult?.roleLabel;

  const focusInput = () => inputRef.current?.focus();

  if (!open) return null;

  const chips = drawerChips(routeKey);

  return (
    // Декоративний backdrop (aria-hidden, канонічний drawer-патерн); закриття з
    // клавіатури — ESC у useDrawerA11y (focus-trap на панелі).
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Помічник CRM"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-hidden bg-background shadow-2xl outline-none sm:w-[440px]"
      >
        {/* header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border-subtle px-4 py-3">
          <div className="flex items-center gap-2 text-[13px] font-bold">
            <Sparkles className="h-4 w-4 text-primary-dark" aria-hidden /> Помічник
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10.5px] text-muted-foreground">
              контекст: {moduleLabel ?? "CRM"}
              {roleLabel ? ` · ${roleLabel.toLowerCase()}` : ""}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрити"
              className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* body */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <AssistantAskBox
            ref={inputRef}
            onAsk={ask}
            disabled={asking}
            size="sm"
            placeholder="Питання про роботу CRM…"
          />

          <AssistantThread
            thread={thread}
            directorMode={directorMode}
            onRetry={ask}
            onAskAnother={focusInput}
          />

          {/* module-aware часті питання + PII-мікрохінт */}
          <div className="border-t border-border-subtle pt-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              Часті питання{moduleLabel ? ` · ${moduleLabel}` : ""}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <button
                  key={c}
                  type="button"
                  disabled={asking}
                  onClick={() => ask(c)}
                  className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] text-foreground hover:border-primary/30 hover:bg-primary-soft disabled:opacity-50"
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              Не вводьте дані клієнтів — чутливе буде замасковано.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
